import { useState, useEffect, useCallback } from 'react';
import {
  getServices,
  getService,
  addService,
  updateService,
  deleteService,
  updateServicesOrder,
  sanitizeServiceData
} from '../firebase/services';
import { updateProfessionalServiceAssociation } from '../firebase/professionalServices';

/**
 * Hook para gerenciar as operações de serviços
 * @param {string} salonId - ID do salão
 * @returns {object} - Métodos e estados para manipular serviços
 */
export const useServices = (salonId) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Carrega todos os serviços do salão
   * @param {string} categoryId - ID opcional da categoria para filtrar
   */
  const loadServices = useCallback(async (categoryId = null) => {
    if (!salonId) {
      setError('ID do salão não fornecido');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const servicesData = await getServices(salonId, categoryId);
      // Filtrar serviços sem ID (proteção extra)
      const validServices = servicesData.filter(service => service && service.id);
      setServices(validServices);
      
      setLoading(false);
    } catch (err) {
      console.error('Erro ao carregar serviços:', err);
      setError(err.message || 'Erro ao carregar serviços');
      setLoading(false);
    }
  }, [salonId]);

  /**
   * Busca um serviço específico
   * @param {string} serviceId - ID do serviço
   * @returns {Promise<object|null>} - Serviço ou null se não encontrado
   */
  const fetchService = async (serviceId) => {
    if (!salonId) {
      setError('ID do salão não fornecido');
      return null;
    }
    
    if (!serviceId) {
      setError('ID do serviço não fornecido');
      return null;
    }
    
    try {
      setError(null);
      return await getService(salonId, serviceId);
    } catch (err) {
      console.error('Erro ao buscar serviço:', err);
      setError(err.message || 'Erro ao buscar serviço');
      return null;
    }
  };

  /**
   * Adiciona um novo serviço
   * @param {object} serviceData - Dados do serviço
   * @returns {Promise<object|null>} - Serviço criado ou null se falhou
   */
  const handleAddService = async (serviceData) => {
    if (!salonId) {
      setError('ID do salão não fornecido');
      return null;
    }
    
    if (!serviceData || !serviceData.nome || !serviceData.categoria) {
      setError('Dados do serviço incompletos');
      return null;
    }
    
    try {
      setError(null);
      
      // Garantir valores numéricos válidos
      const sanitizedData = {
        ...serviceData,
        preco: typeof serviceData.preco === 'number' ? serviceData.preco : 0,
        duracao: typeof serviceData.duracao === 'number' ? serviceData.duracao : 30,
        setupTime: typeof serviceData.setupTime === 'number' ? serviceData.setupTime : 0,
        finishTime: typeof serviceData.finishTime === 'number' ? serviceData.finishTime : 0,
        allowsOverlap: Boolean(serviceData.allowsOverlap),
        ordem: services.length // Adicionar na última posição
      };
      
      const newService = await addService(salonId, sanitizedData);
      
      if (newService && newService.id) {
        // Se houver customizações por profissional, criar as associações
        if (serviceData.professionalCustomizations) {
          // Processar cada profissional individualmente
          for (const [professionalId, customizations] of Object.entries(serviceData.professionalCustomizations)) {
            if (customizations.customPrice !== null || customizations.customDuration !== null) {
              await updateProfessionalServiceAssociation(
                salonId,
                professionalId,
                newService.id,
                {
                  customPrice: customizations.customPrice,
                  customDuration: customizations.customDuration,
                  isActive: true
                }
              );
            }
          }
        }
        
        setServices(prev => [...prev, newService]);
        return newService;
      } else {
        throw new Error('Erro ao adicionar serviço: resposta inválida do servidor');
      }
    } catch (err) {
      console.error('Erro ao adicionar serviço:', err);
      setError(err.message || 'Erro ao adicionar serviço');
      return null;
    }
  };

  /**
   * Atualiza um serviço existente
   * @param {string} serviceId - ID do serviço
   * @param {object} serviceData - Novos dados do serviço
   * @returns {Promise<object|null>} - Serviço atualizado ou null se falhou
   */
  const handleUpdateService = async (serviceId, serviceData) => {
    if (!salonId) {
      setError('ID do salão não fornecido');
      return null;
    }
    
    if (!serviceId) {
      setError('ID do serviço não fornecido');
      return null;
    }
    
    try {
      setError(null);
      
      // Garantir valores numéricos válidos
      const sanitizedData = {
        ...serviceData,
        preco: typeof serviceData.preco === 'number' ? serviceData.preco : 0,
        duracao: typeof serviceData.duracao === 'number' ? serviceData.duracao : 30,
        setupTime: typeof serviceData.setupTime === 'number' ? serviceData.setupTime : 0,
        finishTime: typeof serviceData.finishTime === 'number' ? serviceData.finishTime : 0,
        allowsOverlap: Boolean(serviceData.allowsOverlap)
      };
      
      // Usar a função sanitizeServiceData para garantir que não haja valores undefined
      const safeData = sanitizeServiceData(sanitizedData);
      
      // Importante: Criar uma cópia do serviço atual para referência
      const currentService = services.find(s => s.id === serviceId);
      
      // Chamar a função de API para atualizar o serviço no banco de dados
      const updatedService = await updateService(salonId, serviceId, safeData);
      
      // IMPORTANTE - Criar um objeto limpo que remova campos undefined para evitar problemas
      const cleanUpdatedData = Object.fromEntries(
        Object.entries({
          ...currentService, // Manter campos existentes
          ...safeData,       // Sobrescrever com novos dados sanitizados
          id: serviceId      // Garantir que o ID permaneça o mesmo
        }).filter(([_, v]) => v !== undefined)
      );
      
      if (updatedService) {
        // Se houver customizações por profissional, atualizar as associações
        if (safeData.professionalCustomizations) {
          // Processar cada profissional individualmente
          for (const [professionalId, customizations] of Object.entries(safeData.professionalCustomizations)) {
            // Os valores já estão sanitizados, então podemos usar diretamente
            const customPrice = customizations.customPrice;
            const customDuration = customizations.customDuration;
            
            // Só atualizar se houver valores válidos
            if (customPrice !== null || customDuration !== null) {
              await updateProfessionalServiceAssociation(
                salonId,
                professionalId,
                serviceId,
                {
                  customPrice,
                  customDuration,
                  isActive: true
                }
              );
            }
          }
        }
        
        // Atualizar o estado local com uma abordagem mais segura
        setServices(prevServices => {
          const updated = prevServices.map(service => 
            service.id === serviceId ? cleanUpdatedData : service
          );
          return updated;
        });
        
        return cleanUpdatedData;
      } else {
        throw new Error('Erro ao atualizar serviço: resposta inválida do servidor');
      }
    } catch (err) {
      console.error('Erro ao atualizar serviço:', err);
      setError(err.message || 'Erro ao atualizar serviço');
      return null;
    }
  };

  /**
   * Remove um serviço
   * @param {string} serviceId - ID do serviço
   * @returns {Promise<boolean>} - True se removido com sucesso
   */
  const handleDeleteService = async (serviceId) => {
    if (!salonId) {
      setError('ID do salão não fornecido');
      return false;
    }
    
    if (!serviceId) {
      setError('ID do serviço não fornecido');
      return false;
    }
    
    try {
      setError(null);
      await deleteService(salonId, serviceId);
      setServices(prev => prev.filter(service => service.id !== serviceId));
      return true;
    } catch (err) {
      console.error('Erro ao excluir serviço:', err);
      setError(err.message || 'Erro ao excluir serviço');
      return false;
    }
  };

  /**
   * Atualiza a ordem dos serviços
   * @param {Array} servicesOrder - Array com IDs dos serviços na ordem desejada
   * @returns {Promise<boolean>} - True se atualizado com sucesso
   */
  const handleUpdateOrder = async (servicesOrder) => {
    if (!salonId) {
      setError('ID do salão não fornecido');
      return false;
    }
    
    if (!servicesOrder || !Array.isArray(servicesOrder) || servicesOrder.length === 0) {
      setError('Lista de serviços inválida');
      return false;
    }
    
    // Verificar se todos os IDs são válidos
    if (servicesOrder.some(id => !id)) {
      setError('A lista contém IDs de serviço inválidos');
      return false;
    }
    
    try {
      setError(null);
      await updateServicesOrder(salonId, servicesOrder);
      
      // Reorganizar a lista local de serviços
      const reorderedServices = [];
      servicesOrder.forEach((serviceId, index) => {
        const service = services.find(s => s.id === serviceId);
        if (service) reorderedServices.push({...service, ordem: index});
      });
      
      // Manter serviços que não estão na lista de ordem
      const otherServices = services.filter(s => !servicesOrder.includes(s.id));
      
      setServices([...reorderedServices, ...otherServices]);
      return true;
    } catch (err) {
      console.error('Erro ao atualizar ordem dos serviços:', err);
      setError(err.message || 'Erro ao atualizar ordem dos serviços');
      return false;
    }
  };

  // Carregar serviços na inicialização
  useEffect(() => {
    loadServices();
  }, [loadServices]);

  return {
    services,
    loading,
    error,
    loadServices,
    getService: fetchService,
    addService: handleAddService,
    updateService: handleUpdateService,
    deleteService: handleDeleteService,
    updateServicesOrder: handleUpdateOrder
  };
};

export default useServices; 